/**
 * Save Manager
 * 
 * Handles saving and loading game state to/from localStorage.
 * Future: Can be extended to save to server/database.
 */

export class SaveManager {
    constructor() {
        this.storageKey = 'glorius_factions_game_state';
        this.saveVersion = 1; // Increment when save format changes
    }
    
    /**
     * Save game state to localStorage
     * @param {Object} gameState - GameState object to save
     * @returns {boolean} Success status
     */
    save(gameState) {
        try {
            const saveData = {
                version: this.saveVersion,
                timestamp: Date.now(),
                data: gameState.serialize()
            };
            
            const json = JSON.stringify(saveData);
            localStorage.setItem(this.storageKey, json);
            return true;
        } catch (error) {
            console.error('Error saving game state:', error);
            return false;
        }
    }
    
    /**
     * Load game state from localStorage
     * @returns {Object|null} Deserialized game state data, or null if not found/invalid
     */
    load() {
        try {
            const json = localStorage.getItem(this.storageKey);
            if (!json) {
                return null;
            }
            
            const saveData = JSON.parse(json);
            
            // Check version compatibility
            if (saveData.version !== this.saveVersion) {
                console.warn(`Save version mismatch: ${saveData.version} vs ${this.saveVersion}`);
                // Could implement migration here
            }
            
            return saveData.data;
        } catch (error) {
            console.error('Error loading game state:', error);
            return null;
        }
    }
    
    /**
     * Check if a save exists
     * @returns {boolean} True if save exists
     */
    hasSave() {
        return localStorage.getItem(this.storageKey) !== null;
    }
    
    /**
     * Delete the saved game
     * @returns {boolean} Success status
     */
    deleteSave() {
        try {
            localStorage.removeItem(this.storageKey);
            return true;
        } catch (error) {
            console.error('Error deleting save:', error);
            return false;
        }
    }
    
    /**
     * Get save metadata (timestamp, version)
     * @returns {Object|null} Save metadata or null
     */
    getSaveInfo() {
        try {
            const json = localStorage.getItem(this.storageKey);
            if (!json) {
                return null;
            }
            
            const saveData = JSON.parse(json);
            return {
                version: saveData.version,
                timestamp: saveData.timestamp,
                date: new Date(saveData.timestamp)
            };
        } catch (error) {
            console.error('Error getting save info:', error);
            return null;
        }
    }
    
    /**
     * Export save data as JSON string (for backup/sharing)
     * @returns {string|null} JSON string or null on error
     */
    exportSave() {
        try {
            const json = localStorage.getItem(this.storageKey);
            return json;
        } catch (error) {
            console.error('Error exporting save:', error);
            return null;
        }
    }
    
    /**
     * Import save data from JSON string
     * @param {string} json - JSON string to import
     * @returns {boolean} Success status
     */
    importSave(json) {
        try {
            const saveData = JSON.parse(json);
            
            // Validate structure
            if (!saveData.version || !saveData.data) {
                throw new Error('Invalid save data structure');
            }
            
            // Save to localStorage
            localStorage.setItem(this.storageKey, json);
            return true;
        } catch (error) {
            console.error('Error importing save:', error);
            return false;
        }
    }
}

